import React from 'react';
import {createRoot} from 'react-dom/client';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {persistQueryClient} from '@tanstack/react-query-persist-client';
import {createAsyncStoragePersister} from '@tanstack/query-async-storage-persister';
import '@/styles/rsuite.config.less'
import '@/styles/main.scss'
import CartSidebar, {CartSidebarProps} from '@/components/cart/sidebar/CartSidebar';
import CurrencySelector, {CurrencySelectorProps} from '@/components/currency/selector/CurrencySelector';
import {prefetchSessionInfo} from './api/useSessionInfoQuery';
import VariantPrice, {VariantPriceProps} from '@/components/variant/price/VariantPrice';
import {useCurrenciesStore} from './state/currenciesStore';
import CheckoutAddress from '@/components/checkout/address/CheckoutAddress';
import UserProfileLink, {UserProfileLinkProps} from '@/components/user/profile/link/UserProfileLink';
import UserLogout, {UserLogoutProps} from '@/components/user/logout/UserLogout';
import UserLogin, {UserLoginProps} from '@/components/user/login/UserLogin';
import UserRegister, {UserRegisterProps} from '@/components/user/register/UserRegister';
import UserSidebar, {UserSidebarProps} from '@/components/user/sidebar/UserSidebar';

// One QueryClient for the whole page, shared by all islands
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  },
});

// Persist React Query cache to localStorage
try {
  const persister = createAsyncStoragePersister({storage: window.localStorage});
  persistQueryClient({
    queryClient,
    persister,
    // Keep cached data for up to 24 hours across reloads
    maxAge: 1000 * 60 * 60 * 1,
  });
} catch (e) {
  console.warn('Failed to set up React Query persistence', e);
}

function parseProps<T>(el: HTMLElement): T | undefined {
  try {
    const raw = (el as HTMLElement).dataset.props;
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch (e) {
    console.warn('Failed to parse props for React island', e);
    return undefined;
  }
}

function mountReactIslands() {
  // Initialize currencies store from window data injected by Twig
  try {
    useCurrenciesStore.getState().initFromWindow();
  } catch (e) {
    console.warn('Failed to init currencies store', e);
  }
  // Prefetch session info early to populate CSRF token in the store
  void prefetchSessionInfo(queryClient);

  const currencyEl = document.getElementById('react-sidebar-currency');
  if (currencyEl) {
    const root = createRoot(currencyEl);
    const props = parseProps<CurrencySelectorProps>(currencyEl);

    if (props) {
      root.render(
        <QueryClientProvider client={queryClient}>
          <CurrencySelector {...props} />
        </QueryClientProvider>
      );
    }
  }

  const cartEl = document.getElementById('react-sidebar-cart');
  if (cartEl) {
    const root = createRoot(cartEl);
    const props = parseProps<CartSidebarProps>(cartEl) || {};
    root.render(
      <QueryClientProvider client={queryClient}>
        <CartSidebar {...props} />
      </QueryClientProvider>
    );
  }

  const checkoutEl = document.getElementById('react-sidebar-checkout');
  if (checkoutEl) {
    const root = createRoot(checkoutEl);
    const props = parseProps<any>(checkoutEl) || {};
    root.render(
      <QueryClientProvider client={queryClient}>
        <div>
          <CheckoutAddress {...props} />
        </div>
      </QueryClientProvider>
    );
  }

  // Mount all VariantPrice islands
  const priceEls = Array.from(document.getElementsByTagName('variantprice')) as HTMLElement[];
  priceEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<VariantPriceProps>(el);
    if (!props) return;
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <VariantPrice {...props} />
      </QueryClientProvider>
    );
  });

  // Mount all UserProfileLink islands
  const profileEls = Array.from(document.getElementsByTagName('userprofilelink')) as HTMLElement[];
  profileEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<UserProfileLinkProps>(el) || {};
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <UserProfileLink {...props} />
      </QueryClientProvider>
    );
  });

  // Mount all UserLogin islands
  const loginEls = Array.from(document.getElementsByTagName('userlogin')) as HTMLElement[];
  loginEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<UserLoginProps>(el) || {};
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <UserLogin {...props} />
      </QueryClientProvider>
    );
  });

  // Mount all UserRegister islands
  const registerEls = Array.from(document.getElementsByTagName('userregister')) as HTMLElement[];
  registerEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<UserRegisterProps>(el) || {};
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <UserRegister {...props} />
      </QueryClientProvider>
    );
  });

  // Mount all UserLogout islands
  const logoutEls = Array.from(document.getElementsByTagName('userlogout')) as HTMLElement[];
  logoutEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<UserLogoutProps>(el) || {};
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <UserLogout {...props} />
      </QueryClientProvider>
    );
  });

  // Mount all UserSidebar islands
  const sidebarEls = Array.from(document.getElementsByTagName('usersidebar')) as HTMLElement[];
  sidebarEls.forEach((el) => {
    if ((el as any)._mounted) return;
    const props = parseProps<UserSidebarProps>(el) || {};
    const root = createRoot(el);
    (el as any)._mounted = true;
    root.render(
      <QueryClientProvider client={queryClient}>
        <UserSidebar {...props} />
      </QueryClientProvider>
    );
  });
}

// Mount on DOMContentLoaded to work with Craft-rendered pages
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountReactIslands);
} else {
  mountReactIslands();
}
